import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { currentTerm } from '../utils/brandingUtils';
import { EMPLOYEE_SAFE_COLUMNS, EMPLOYEE_COMP_EMBED, withComp } from '../utils/employeeCompensation';
import { companyDateKey } from '../utils/dateFormatters';

// Environment variables for beta (used in fallback queries)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Fetches employees list with user details.
 * Replicates the exact query from loadEmployees in Employees.js
 */
const fetchEmployeesList = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // admin-get-company-users is STARTED here, alongside the employees read, instead of after it
  // (page speed 2026-10-25: the function takes ~0.4–0.9 s and the page waited for the list
  // first). Its input does not depend on the list — the function derives the company from the
  // caller's token. The result is still only USED when the list has user ids, exactly as before;
  // a failure is settled here and handled by the same fallback below.
  const companyUsersRequest = (async () => {
    const { data: { session } } = await supabase.auth.getSession();

    const usersResponse = await fetch(`${SUPABASE_URL}/functions/v1/admin-get-company-users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!usersResponse.ok) {
      const errorData = await usersResponse.json();
      throw new Error(errorData.error || 'Failed to fetch users');
    }

    return usersResponse.json();
  })();
  // Settled up front so an early throw below (employees error) leaves no unhandled rejection.
  companyUsersRequest.catch(() => {});

  // ✅ CORRECT: Query employees table (employment records) - anchor table
  const { data: employeesData, error: empError } = await supabase
    .from('employees')
    // The pay/PII columns moved to employee_compensation; select('*') on employees now 42501s.
    .select(`${EMPLOYEE_SAFE_COLUMNS},${EMPLOYEE_COMP_EMBED}`)
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (empError) {
    console.error('❌ Supabase error:', empError);
    throw empError;
  }

  console.log('📋 Raw employees data:', employeesData);

  // Get unique user_ids to fetch user details
  const userIds = employeesData
    .filter(emp => emp.user_id)
    .map(emp => emp.user_id);
    
  console.log('🔍 USER_IDS TO FETCH:', userIds);
  console.log('🔍 Current user company_id:', companyId);

  let userDataMap = {};
  
  if (userIds.length > 0) {
    // ✅ FIX: Use Edge Function to fetch users (bypasses RLS issues)
    try {
      console.log('🔐 Calling admin-get-company-users Edge Function...');
      const usersResult = await companyUsersRequest;
      const usersData = usersResult.users;
      
      console.log('🔍 USERS DATA FETCHED:', usersData);
      // Build a map for quick lookup
      (usersData || []).forEach(u => {
        userDataMap[u.id] = u;
        console.log(`🔍 User ${u.id} phone: "${u.phone ?? '<none>'}"`);
      });
    } catch (userError) {
      console.warn('⚠️ Could not fetch user details via Edge Function:', userError);
      // Fallback: Try direct query (may still fail due to RLS)
      try {
        const { data: usersData, error: directError } = await supabase
          .from('users')
          .select('id, auth_user_id, email, role, phone, first_name, last_name, status')
          .in('id', userIds);
        
        if (!directError && usersData) {
          console.log('🔍 Fallback: Direct query succeeded');
          (usersData || []).forEach(u => {
            userDataMap[u.id] = u;
            console.log(`🔍 User ${u.id} phone: "${u.phone ?? '<none>'}"`);
          });
        }
      } catch (fallbackError) {
        console.warn('⚠️ Fallback query also failed:', fallbackError);
      }
    }
  }

  // Transform data: employees is anchor, users provides auth details
  const mappedData = (employeesData || []).map(rawEmp => {
    const emp = withComp(rawEmp);
    const userData = userDataMap[emp.user_id] || {};

    console.log(`🔍 MAPPING emp.user_id=${emp.user_id}: userData.phone="${userData.phone}"`);

    return {
      // Primary key - use employees.id for employee-specific operations
      id: emp.id,
      user_id: emp.user_id,

      // FETCHED, SORTED ON, AND THEN DROPPED. This mapper is an explicit allow-list,
      // and created_at was not one of its ~25 keys even though the query selects * and
      // orders by it. So every consumer saw undefined, and the detail panel's "Joined"
      // field took its falsy branch for 100% of employees -- rendering "-" and
      // "Unknown" rather than a wrong date on one row. That is the giveaway for this
      // shape of bug: it fails uniformly, which reads as "no data yet" instead of a
      // defect (Employees.js:2324 and getTimeSinceJoined at :736).
      created_at: emp.created_at,

      // Auth info from users table
      auth_user_id: userData.auth_user_id || null,
      email: userData.email || `${emp.first_name?.toLowerCase() || 'employee'}@example.com`,
      // #4248: role lives on users only. An employee with no login (user_id NULL) has none, and
      // the old `|| 'technician'` invented one — /employees said Technician while Org Structure
      // said '-'. Null here; the displays say "No login".
      role: userData.role || null,

      // Employment data from employees table (source of truth)
      employee_number: emp.employee_number || '',
      job_title: emp.job_title || userData.role || currentTerm('worker'),
      department: emp.department || '',
      hourly_rate: emp.hourly_rate || 0,
      overtime_rate: emp.overtime_rate || 0,
      pay_type: emp.pay_type || 'hourly',
      // Board #452. This mapping is EXPLICIT, so a column missing from it reads as undefined
      // rather than throwing — and the edit form would then show a salaried employee a blank
      // salary and write their derived cost rate back as 0 on the next save. `?? null` and not
      // `|| 0`: absent is not the same as a salary of nothing, and only hourly staff have none.
      annual_salary: emp.annual_salary ?? null,
      capacity_hours_per_day: emp.capacity_hours_per_day || 8,
      is_schedulable: emp.is_schedulable !== undefined ? emp.is_schedulable : true,
      status: emp.status || userData.status || 'active',
      hire_date: emp.hire_date || '',
      termination_date: emp.termination_date || '',
      employee_type: emp.employee_type || 'full-time',
      manager_id: emp.manager_id || '',

      // Emergency contact
      emergency_contact_name: emp.emergency_contact_name || '',
      emergency_contact_phone: emp.emergency_contact_phone || '',
      emergency_contact_relationship: emp.emergency_contact_relationship || '',

      // Other employee fields
      date_of_birth: emp.date_of_birth || '',
      pto_balance_hours: emp.pto_balance_hours || 0,
      // certifications is a text[] array in DB - convert to comma-separated string for display
      certifications: Array.isArray(emp.certifications) ? emp.certifications.join(', ') : (emp.certifications || ''),
      notes: emp.notes || '',

      // Name from employees table (convenience), fallback to users
      full_name: (emp.first_name || emp.last_name)
        ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
        : (userData.name || userData.first_name || 'Unknown User'),
      first_name: emp.first_name || userData.first_name || '',
      last_name: emp.last_name || userData.last_name || '',

      // Contact from users table
      phone: userData.phone || '',

      // DEBUG: Log phone loading
      _debug_userId: emp.user_id,
      _debug_userPhone: userData.phone,
      _debug_userDataKeys: Object.keys(userData),
    };
  });

  console.log('📋 Mapped employee data - checking phones:', mappedData.map(e => ({ id: e.id, user_id: e.user_id, phone: e.phone })));
  return mappedData;
};

/**
 * Fetches PTO policies and ledger entries.
 * Replicates the exact query from loadPTOData in Employees.js
 */
const fetchPTOData = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // Load PTO policies
  const { data: policies, error: policiesError } = await supabase
    .from('pto_policies')
    .select('*')
    .order('created_at', { ascending: false });

  if (policiesError) {
    console.error('Error loading PTO policies:', policiesError);
    throw policiesError;
  }

  // PTO ledger table does not exist yet — skip query
  let ledger = [];

  return {
    policies: policies || [],
    ledger: ledger
  };
};

/**
 * Time off that actually BLOCKS SCHEDULING - upcoming or in progress, not withdrawn.
 *
 * The Employees page's card was a hardcoded `const ptoRequestsCount = 0` - no data
 * source at all - while employee_time_off held 3 rows.
 *
 * It counts BLOCKING time off, NOT "pending requests". There is no approval workflow in
 * this product and there is not meant to be (operator, 2026-08-24: the PTO system "is
 * just for scheduling. we dont do anything with it except not allow scheduling"). The
 * approval iteration was abandoned - services/PTOServiceFinal.js has zero importers and
 * nothing has ever written APPROVED. Counting "pending" would advertise a queue that
 * nobody can action.
 *
 * Mirrors the predicate the slot functions use after migration 20260824a: anything not
 * REJECTED or CANCELLED blocks. Uppercase because status is the ENUM
 * time_off_status_enum - querying pg_constraint for a CHECK finds nothing and proves
 * nothing.
 */
const fetchBlockingTimeOffCount = async (companyId) => {
  if (!companyId) return 0;
  const supabase = getSupabaseClient();
  const { count, error } = await supabase
    .from('employee_time_off')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .not('status', 'in', '(REJECTED,CANCELLED)')
    .gte('ends_at', new Date().toISOString());
  if (error) throw error;
  return count || 0;
};

/**
 * Employees with time off covering TODAY, for the team list's "Off today" badge and filter
 * (PRODUCT_DECISIONS "Employees: Off today", 2026-10-24; #6154).
 *
 * "In effect" is the same predicate as the blocking count above and the slot functions: there
 * is no approval step (20260824a), so every request that is not REJECTED/CANCELLED takes the
 * person off. The ruling's "approved time off" means exactly that set; filtering on APPROVED
 * would match nothing, because nothing writes it.
 *
 * Today is the COMPANY's calendar day. starts_at/ends_at are wall-clock values parked at UTC
 * (see formatWallClockDate in utils/dateUtils), so the day is compared at UTC: a full day off
 * is stored 00:00:00Z–23:59:59Z of that date.
 */
const fetchOffTodayEmployeeIds = async (companyId, today) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('employee_time_off')
    .select('employee_id')
    .eq('company_id', companyId)
    .not('status', 'in', '(REJECTED,CANCELLED)')
    .lte('starts_at', `${today}T23:59:59.999Z`)
    .gte('ends_at', `${today}T00:00:00Z`);
  if (error) throw error;
  return new Set((data || []).map((r) => r.employee_id));
};

export const useOffTodayEmployeeIds = (companyId) => {
  const today = companyDateKey();
  return useQuery({
    queryKey: ['employee-time-off-today', companyId, today],
    queryFn: () => fetchOffTodayEmployeeIds(companyId, today),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Employees does not refresh automatically
    enabled: !!companyId && !!today,
  });
};

export const useBlockingTimeOffCount = (companyId) => {
  return useQuery({
    queryKey: ['employee-time-off-blocking', companyId],
    queryFn: () => fetchBlockingTimeOffCount(companyId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Employees does not refresh automatically
    enabled: !!companyId,
  });
};

/**
 * React Query hook for fetching employees list.
 */
export const useEmployeesList = (companyId) => {
  return useQuery({
    queryKey: ['employees-list', companyId],
    queryFn: () => fetchEmployeesList(companyId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Employees does not refresh automatically
    enabled: !!companyId,
  });
};
